import React from "react";
import { motion } from "framer-motion";
import {
  Link as LinkIcon,
  Sparkles,
  Gift,
  Star,
  ChevronRight,
  Zap,
  MousePointer2
} from "lucide-react";
import Navbar from "../components/layout/navbar";
import Footer from "../components/layout/footer";
import GoogleLoginButton from "../components/buttons/GoogleLoginButton";

const HeroSection = ({ scrollToFeatures }) => {
  return (
    <section className="relative min-h-[90vh] w-full flex flex-col items-center justify-center overflow-hidden pt-24 pb-32">
      {/* Softer Background Glows */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-[5%] left-[-5%] w-[50%] h-[51%] bg-indigo-500/20 blur-[120px] rounded-full animate-pulse"></div>
        <div className="absolute bottom-[5%] right-[-5%] w-[50%] h-[52%] bg-blue-500/10 blur-[120px] rounded-full animate-pulse" style={{ animationDelay: '2.5s' }}></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 flex flex-col items-center text-center">
        {/* Friendlier Beta Batch */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-slate-800/50 border border-slate-700 backdrop-blur-xl mb-12 shadow-2xl"
        >
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-500 text-white shadow-lg shadow-indigo-500/40">
            <Star size={10} fill="currentColor" />
          </div>
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.1em] text-slate-300">
            Limited Beta: <span className="text-white ml-1">1 Free Rapor & 3 Mesaj</span>
          </p>
        </motion.div>

        {/* Headline - Softer Gradient */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-5xl md:text-8xl font-black tracking-tight leading-[1] mb-8 bg-clip-text text-transparent bg-gradient-to-b from-white via-white to-slate-400"
        >
          Better Research <br /> for <span className="text-indigo-400">Everyone.</span>
        </motion.h1>

        {/* Subline - Higher contrast secondary text */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed mb-14 font-medium"
        >
          Transform dense academic papers into clear, actionable intelligence. <br className="hidden md:block" />
          Designed for simplicity, built for the modern scholar.
        </motion.p>

        {/* Action Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col items-center gap-8"
        >
          <div className="scale-110 md:scale-125 hover:scale-130 transition-all duration-500 drop-shadow-[0_0_20px_rgba(79,70,229,0.3)]">
            <GoogleLoginButton />
          </div>

          <button
            onClick={scrollToFeatures}
            className="flex items-center gap-2 text-slate-500 hover:text-indigo-400 transition-all text-[10px] font-black uppercase tracking-[0.2em] mt-10 group"
          >
            Capabilities
            <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>

      {/* Visual Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 opacity-30">
        <div className="w-px h-12 bg-gradient-to-b from-transparent via-slate-400 to-transparent"></div>
        <MousePointer2 size={12} className="text-slate-400" />
      </div>
    </section>
  );
};

const FeaturesSection = React.forwardRef((props, ref) => {
  const features = [
    {
      title: "Fast Summaries",
      desc: "Instantly extract the core essence, strengths, and weaknesses of any academic publication.",
      icon: <LinkIcon size={22} />,
      color: "indigo"
    },
    {
      title: "Contextual Chat",
      desc: "Ask the paper anything. Our AI finds the exact evidence you need, grounded in the text.",
      icon: <Zap size={22} />,
      color: "blue"
    },
    {
      title: "Comparison",
      desc: "Connect multiple papers to find common themes, conflicting metrics and deeper patterns.",
      icon: <Sparkles size={22} />,
      color: "violet"
    }
  ];

  return (
    <section ref={ref} className="px-6 py-40 relative">
      <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-10">
        {features.map((f, i) => (
          <motion.div
            key={i}
            whileHover={{ y: -8 }}
            className="relative bg-slate-900/40 border border-slate-800 rounded-[2.5rem] p-10 hover:bg-slate-800/40 hover:border-indigo-500/30 transition-all duration-500 shadow-xl group"
          >
            <div className={`w-12 h-12 bg-slate-800 text-indigo-400 rounded-xl flex items-center justify-center mb-10 group-hover:bg-indigo-500 group-hover:text-white transition-all duration-500 shadow-inner`}>
              {f.icon}
            </div>
            <h3 className="text-2xl font-bold mb-4 tracking-tight text-white">{f.title}</h3>
            <p className="text-slate-400 leading-relaxed font-medium text-sm">
              {f.desc}
            </p>

            <div className="mt-10 pt-8 border-t border-slate-800/50 flex justify-between items-center">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 group-hover:text-indigo-400 transition-colors">Details</span>
              <ChevronRight size={14} className="text-slate-700 group-hover:text-indigo-400" />
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
});

export default function Welcome() {
  const featuresRef = React.useRef(null);

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#0f172a] text-slate-100 selection:bg-indigo-500/30 font-sans">
      <Navbar />
      <HeroSection scrollToFeatures={scrollToFeatures} />
      <FeaturesSection ref={featuresRef} />

      {/* Footer - Friendlier Branding */}
      <div className="max-w-7xl mx-auto w-full px-8 py-24 border-t border-slate-800">
        <div className="flex flex-col md:flex-row justify-between items-center gap-12">
          <div className="flex flex-col items-center md:items-start gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center font-black italic text-white shadow-lg">P</div>
              <span className="font-bold text-xl tracking-tight text-white">PaperLens</span>
            </div>
            <p className="text-slate-500 text-sm max-w-xs text-center md:text-left leading-relaxed">Making academic research accessible one insight at a time.</p>
          </div>
          <div className="flex items-center gap-8 text-[11px] font-bold text-slate-400">
            <a href="#" className="hover:text-indigo-400 transition-colors">Docs</a>
            <a href="#" className="hover:text-indigo-400 transition-colors">Privacy</a>
            <a href="#" className="hover:text-indigo-400 transition-colors">Changelog</a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
